import { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { 
  UploadResponse, 
  Histogram, 
  MetadataFilters,
  DefectTypeDistribution as DefectTypeDistributionType, 
  WelderPerformance, 
  TimeSeriesDataPoint, 
  RCAAnalysis as RCAAnalysisType, 
  LocationAnalysis, 
  ShiftComparison as ShiftComparisonType,
  WelderDetailedAnalysis
} from '../types/stats';
import { NdtStatisticsSection } from './NdtStatisticsSection';
import { HistogramBarChart, EvaluationSelection } from './charts/HistogramBarChart';
import { StartDistanceCharts } from './charts/StartDistanceCharts';
import { FilterPanel } from './FilterPanel';
import { DefectTypeDistribution } from './charts/DefectTypeDistribution';
import { WelderPerformanceDashboard } from './charts/WelderPerformanceDashboard';
import { TimeSeriesTrends } from './charts/TimeSeriesTrends';
import { RCAAnalysis } from './charts/RCAAnalysis';
import { LocationPositionalAnalysis } from './charts/LocationPositionalAnalysis';
import { ShiftComparison } from './charts/ShiftComparison';
import { WelderDefectHeatmap } from './charts/WelderDefectHeatmap';
import { PositionDefectRadar } from './charts/PositionDefectRadar';
import { IndividualWelderAnalysis } from './charts/IndividualWelderAnalysis';
import { InsightsPanel } from './InsightsPanel';
import { MLInsightsPanel } from './MLInsightsPanel';
import { fetchAIInsights } from '../../../redux/NDT Analytics/actions';
import type { NdtCategoryData, CategoryFieldConfig, PipeOption } from './FilterPanel';
import { AnalyticsSegmentedControl } from '../../weld-analytics/components/shared/AnalyticsSegmentedControl';

interface ProjectDetailViewProps {
  data: UploadResponse | null;
  selectedProject: string;
  filters: MetadataFilters | null;
  selectedFilters: {
    project?: string | null;
    subProject?: string[] | string | null;
    pipeId?: string[] | string | null;
    pipeDiameter?: string | null;
    wallThickness?: string[] | string | null;
    shift?: string | null;
    location?: string[] | string | null;
    criticalWeld?: string | null;
    wpsNumber?: string[] | string | null;
  };
  onFilterChange: (filters: {
    project?: string | null;
    subProject?: string[] | string | null;
    pipeId?: string[] | string | null;
    pipeDiameter?: string | null;
    wallThickness?: string[] | string | null;
    shift?: string | null;
    location?: string[] | string | null;
    criticalWeld?: string | null;
    wpsNumber?: string[] | string | null;
  }) => void;
  onBack: () => void;
  isLoading?: boolean;
  error?: string | null;
  isNoRecordsFound?: boolean;
  currentFile?: File | null;
  onDataUpdate?: (data: UploadResponse) => void;
  projectId?: string | number | null;
  categoryData?: NdtCategoryData | null;
  categoryFields?: CategoryFieldConfig[];
  pipeOptions?: PipeOption[];
}

const VISUAL_MEASUREMENT_ORDER = ['Length (mm)', 'Depth (mm)', 'Height (mm)', 'Start Distance'];

const formatMeasurementLabel = (label: string) => label.replace(/\s*\(mm\)\s*/gi, '').trim();

type DashboardType = 'measurements' | 'defect-types' | 'welders' | 'time-trends' | 'location' | 'rca';

// Measurement Dashboard Component
const MeasurementDashboard = ({
  histograms
}: {
  histograms: Record<string, Histogram>;
}) => {
  const histogramOptions = useMemo(() => {
    const ordered = VISUAL_MEASUREMENT_ORDER.filter((key) => histograms[key]);
    const remaining = Object.keys(histograms).filter((key) => !ordered.includes(key));
    return [...ordered, ...remaining].map((key) => ({
      key,
      label: formatMeasurementLabel(key) || key,
    }));
  }, [histograms]);

  const [selectedMeasurement, setSelectedMeasurement] = useState<string>(histogramOptions[0]?.key ?? '');
  const [evaluationSelection, setEvaluationSelection] = useState<EvaluationSelection>({
    Accepted: true,
    Rejected: true,
  });

  const histogram = histograms[selectedMeasurement];
  const isStartDistance = selectedMeasurement.toLowerCase().includes('start');
  const displayHistogram = useMemo(() => {
    if (!histogram) return histogram;
    if (!isStartDistance) return histogram;
    return {
      ...histogram,
      bins: histogram.bins.map((bin) => ({
        ...bin,
        label: formatMeasurementLabel(bin.value.toString()),
      })),
    };
  }, [histogram, isStartDistance]);

  if (histogramOptions.length === 0) {
    return (
      <p className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
        No measurement data available.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
      <AnalyticsSegmentedControl
        ariaLabel="Measurement"
        tabs={histogramOptions.map((option) => ({ id: option.key, label: option.label }))}
        activeId={selectedMeasurement}
        onChange={setSelectedMeasurement}
      />

      <AnalyticsSegmentedControl
        ariaLabel="Evaluation"
        tabs={(Object.keys(evaluationSelection) as Array<keyof EvaluationSelection>).map((key) => ({
          id: key,
          label: key,
        }))}
        isActive={(id) => evaluationSelection[id as keyof EvaluationSelection]}
        onChange={(id) =>
          setEvaluationSelection((prev) => {
            const key = id as keyof EvaluationSelection;
            const enabledCount = Object.values(prev).filter(Boolean).length;
            const nextValue = !prev[key];
            if (!nextValue && enabledCount === 1) return prev;
            return { ...prev, [key]: nextValue };
          })
        }
      />
      </div>

      {isStartDistance ? (
        displayHistogram && (
          <StartDistanceCharts histogram={displayHistogram} evaluations={evaluationSelection} />
        )
      ) : (
        histogram && (
          <HistogramBarChart
            histogram={histogram}
            evaluations={evaluationSelection}
            title={`${formatMeasurementLabel(selectedMeasurement)} distribution`}
          />
        )
      )}
    </div>
  );
};

// Defect Type Dashboard Component
const DefectTypeDashboard = ({
  defectTypeDistribution
}: {
  defectTypeDistribution?: DefectTypeDistributionType[];
}) => {
  if (!defectTypeDistribution || defectTypeDistribution.length === 0) {
    return (
      <p className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
        No defect type data available.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <DefectTypeDistribution data={defectTypeDistribution} />
    </div>
  );
};

export const WelderDashboard = ({
  welderPerformance,
  welderDetailedAnalysis
}: {
  welderPerformance?: WelderPerformance[];
  welderDetailedAnalysis?: WelderDetailedAnalysis;
}) => {
  const [activeView, setActiveView] = useState<'performance' | 'heatmap-defects' | 'heatmap-positions' | 'position-radar' | 'individual'>('performance');

  const hasPerformance = welderPerformance && welderPerformance.length > 0;
  const hasDetailed = welderDetailedAnalysis &&
    Object.keys(welderDetailedAnalysis.welderDefectMatrix || {}).length > 0;

  if (!hasPerformance && !hasDetailed) {
    return (
      <p className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
        No welder performance data available.
      </p>
    );
  }

  const views = [
    { id: 'performance', label: 'Performance', available: hasPerformance },
    { id: 'heatmap-defects', label: 'Welder vs Defects', available: hasDetailed },
    { id: 'heatmap-positions', label: 'Welder vs Positions', available: hasDetailed },
    { id: 'position-radar', label: 'Position vs Defects', available: hasDetailed && welderDetailedAnalysis?.allPositions && welderDetailedAnalysis.allPositions.length > 0 },
    { id: 'individual', label: 'Individual Analysis', available: hasDetailed },
  ].filter(v => v.available);

  return (
    <div className="space-y-6">
      {/* View Navigation */}
      <AnalyticsSegmentedControl
        ariaLabel="Welder performance view"
        tabs={views.map((view) => ({ id: view.id, label: view.label }))}
        activeId={activeView}
        onChange={(id) => setActiveView(id as typeof activeView)}
      />

      {/* View Content */}
      <div className="mt-6">
        {activeView === 'performance' && hasPerformance && (
          <WelderPerformanceDashboard data={welderPerformance} />
        )}
        {activeView === 'heatmap-defects' && hasDetailed && (
          <WelderDefectHeatmap data={welderDetailedAnalysis} showPositions={false} />
        )}
        {activeView === 'heatmap-positions' && hasDetailed && (
          <WelderDefectHeatmap data={welderDetailedAnalysis} showPositions={true} />
        )}
        {activeView === 'position-radar' && hasDetailed && (
          <PositionDefectRadar data={welderDetailedAnalysis} />
        )}
        {activeView === 'individual' && hasDetailed && (
          <IndividualWelderAnalysis data={welderDetailedAnalysis} />
        )}
      </div>
    </div>
  );
};

// Time & Trends Dashboard Component
const TimeTrendsDashboard = ({
  timeSeriesTrends,
  shiftComparison
}: {
  timeSeriesTrends?: TimeSeriesDataPoint[];
  shiftComparison?: ShiftComparisonType[];
}) => {
  const hasData = (timeSeriesTrends && timeSeriesTrends.length > 0) ||
    (shiftComparison && shiftComparison.length > 0);

  if (!hasData) {
    return (
      <p className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
        No time series or trend data available.
      </p>
    );
  }

  return (
    <div className="space-y-8">
      {timeSeriesTrends && timeSeriesTrends.length > 0 && (
        <TimeSeriesTrends data={timeSeriesTrends} />
      )}
      {shiftComparison && shiftComparison.length > 0 && (
        <ShiftComparison data={shiftComparison} />
      )}
    </div>
  );
};

// Location Dashboard Component
const LocationDashboard = ({
  locationAnalysis
}: {
  locationAnalysis?: LocationAnalysis[];
}) => {
  if (!locationAnalysis || locationAnalysis.length === 0) {
    return (
      <p className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
        No location analysis data available.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <LocationPositionalAnalysis data={locationAnalysis} />
    </div>
  );
};

// RCA Dashboard Component
const RCADashboard = ({
  rcaAnalysis
}: {
  rcaAnalysis?: RCAAnalysisType[];
}) => {
  if (!rcaAnalysis || rcaAnalysis.length === 0) {
    return (
      <p className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
        No root cause analysis data available.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <RCAAnalysis data={rcaAnalysis} />
    </div>
  );
};

// Main Visualization Panel with Dashboard Navigation
export const VisualizationPanel = ({
  histograms,
  insights
}: {
  histograms: Record<string, Histogram>;
  insights?: UploadResponse['insights'];
}) => {
  const [activeDashboard, setActiveDashboard] = useState<DashboardType>('measurements');

  const hasHistograms = Object.keys(histograms).length > 0;
  const hasDefectTypes = insights?.defectTypeDistribution && insights.defectTypeDistribution.length > 0;
  const hasWelders = insights?.welderPerformance && insights.welderPerformance.length > 0;
  const hasTimeTrends = (insights?.timeSeriesTrends && insights.timeSeriesTrends.length > 0) ||
    (insights?.shiftComparison && insights.shiftComparison.length > 0);
  const hasLocation = insights?.locationAnalysis && insights.locationAnalysis.length > 0;
  const hasRCA = insights?.rcaAnalysis && insights.rcaAnalysis.length > 0;

  // Determine available dashboards
  const availableDashboards = useMemo(() => {
    const dashboards: Array<{ id: DashboardType; label: string; available: boolean }> = [
      { id: 'measurements', label: 'Measurements', available: hasHistograms ?? false },
      { id: 'defect-types', label: 'Defect Types', available: hasDefectTypes ?? false },
      { id: 'welders', label: 'Welders', available: hasWelders ?? false },
      { id: 'time-trends', label: 'Time & Trends', available: hasTimeTrends ?? false },
      { id: 'location', label: 'Location', available: hasLocation ?? false },
      { id: 'rca', label: 'Root Cause', available: hasRCA ?? false },
    ];
    return dashboards.filter(d => d.available);
  }, [hasHistograms, hasDefectTypes, hasWelders, hasTimeTrends, hasLocation, hasRCA]);

  // Auto-select first available dashboard if current is not available
  useEffect(() => {
    if (availableDashboards.length > 0 && !availableDashboards.find(d => d.id === activeDashboard)) {
      setActiveDashboard(availableDashboards[0].id);
    }
  }, [availableDashboards, activeDashboard]);

  if (availableDashboards.length === 0) {
    return (
      <p className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
        No visualization data available.
      </p>
    );
  }

  const renderDashboardContent = () => {
    switch (activeDashboard) {
      case 'measurements':
        return <MeasurementDashboard histograms={histograms} />;
      case 'defect-types':
        return <DefectTypeDashboard defectTypeDistribution={insights?.defectTypeDistribution} />;
      case 'welders':
        return (
          <WelderDashboard
            welderPerformance={insights?.welderPerformance}
            welderDetailedAnalysis={insights?.welderDetailedAnalysis}
          />
        );
      case 'time-trends':
        return (
          <TimeTrendsDashboard
            timeSeriesTrends={insights?.timeSeriesTrends}
            shiftComparison={insights?.shiftComparison}
          />
        );
      case 'location':
        return <LocationDashboard locationAnalysis={insights?.locationAnalysis} />;
      case 'rca':
        return <RCADashboard rcaAnalysis={insights?.rcaAnalysis} />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Dashboard Navigation */}
      <AnalyticsSegmentedControl
        ariaLabel="NDT visualizations"
        tabs={availableDashboards.map((dashboard) => ({
          id: dashboard.id,
          label: dashboard.label,
        }))}
        activeId={activeDashboard}
        onChange={(id) => setActiveDashboard(id as DashboardType)}
      />

      {/* Dashboard Content */}
      <div className="mt-6">
        {renderDashboardContent()}
      </div>
    </div>
  );
};

export const ProjectDetailView = ({
  data,
  selectedProject,
  filters,
  selectedFilters,
  onFilterChange,
  onBack,
  isLoading = false,
  error = null,
  isNoRecordsFound = false,
  currentFile,
  onDataUpdate,
  projectId,
  categoryData = null,
  categoryFields = [],
  pipeOptions = [],
}: ProjectDetailViewProps) => {
  const dispatch = useDispatch();
  const { aiInsightsData, isFetchingAIInsights, aiInsightsError } = useSelector(
    (state: any) => state.ndtAnalytics || {}
  );

  const [activeTab, setActiveTab] = useState<'statistics' | 'visualizations' | 'welder-performance' | 'insights' | 'ml-insights'>('statistics');

  // Fetch AI Insights when ml-insights tab is active
  useEffect(() => {
    if (activeTab === 'ml-insights' && !aiInsightsData && !isFetchingAIInsights) {
      const toArr = (v: string[] | string | null | undefined) => {
        if (v == null) return null;
        if (Array.isArray(v)) return v.length > 0 ? v : null;
        return v !== '' ? [String(v)] : null;
      };
      const toPipeIdApi = (pipeId: string[] | string | null | undefined): number | number[] | null => {
        if (pipeId == null) return null;
        const ids = (Array.isArray(pipeId) ? pipeId : [pipeId])
          .filter((id) => id != null && id !== '')
          .map(Number)
          .filter((id) => !Number.isNaN(id));
        if (ids.length === 0) return null;
        if (ids.length === 1) return ids[0];
        return ids;
      };
      const payload = {
        project: projectId || null,
        pipe_id: toPipeIdApi(selectedFilters?.pipeId),
        sub_project: toArr(selectedFilters?.subProject),
        pipe_diameter: selectedFilters?.pipeDiameter || null,
        wall_thickness: toArr(selectedFilters?.wallThickness),
        shift: selectedFilters?.shift || null,
        location: toArr(selectedFilters?.location),
        critical_weld: selectedFilters?.criticalWeld || null,
        wps_number: toArr(selectedFilters?.wpsNumber),
        welder_number: null,
        welder_repair_number: null,
        evaluation: null,
        defect_type: null,
        start_date: null,
        end_date: null,
        limit: null
      };
      console.log(' Ml api payload', payload);
      dispatch(fetchAIInsights(payload));
    }
  }, [activeTab, aiInsightsData, isFetchingAIInsights, projectId, selectedFilters, dispatch]);

  const renderTabContent = () => {
    if (activeTab === 'visualizations') {
      if (!data) {
        return (
          <p className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
            No data available.
          </p>
        );
      }
      const histograms = data.histograms ?? {};
      const insights = data.insights;
      return <VisualizationPanel histograms={histograms} insights={insights} />;
    }

    if (activeTab === 'welder-performance') {
      if (!data) {
        return (
          <p className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
            No data available.
          </p>
        );
      }
      const insights = data.insights;
      return (
        <WelderDashboard
          welderPerformance={insights?.welderPerformance}
          welderDetailedAnalysis={insights?.welderDetailedAnalysis}
        />
      );
    }

    if (activeTab === 'insights') {
      return <InsightsPanel insights={data?.aiInsights || null} isLoading={isLoading} />;
    }

    if (activeTab === 'ml-insights') {
      // Use Redux AI Insights data directly (API returns ML insights data structure)
      // The response structure is: { summary, patterns, correlations, anomalies, ... }
      const mlInsights = aiInsightsData?.mlInsights || null;
      // console.log('ML Insights data being passed to MLInsightsPanel:', mlInsights);
      // console.log('aiInsightsData from Redux:', aiInsightsData);
      // console.log('isFetchingAIInsights:', isFetchingAIInsights);
      // console.log('ML Insights Panel - Current data:', {
      //   hasMlInsights: aiInsightsData?.mlInsights,
      //   patterns: aiInsightsData?.mlInsights?.patterns?.length || 0,
      //   correlations: aiInsightsData?.mlInsights?.correlations?.length || 0,
      //   anomalies: aiInsightsData?.mlInsights?.anomalies?.length || 0,
      // });
      return   <MLInsightsPanel insights={mlInsights} isLoading={isFetchingAIInsights || isLoading} /> ;
    }

    if (!data) {
      return (
        <p className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
          No data available.
        </p>
      );
    }

    return <NdtStatisticsSection data={data} />;
  };

  const contentArea = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center py-12 whitespace-nowrap gap-2 items-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]"></div>
                <span className="text-gray-600 text-lg">Loading NDT Analytics data...</span>
        </div>
      );
    }
    if (isNoRecordsFound) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-gray-600 text-lg mb-2">No records found for this project.</p>
            <p className="text-gray-500 text-sm">Please add NDT records for this project.</p>
          </div>
        </div>
      );
    }
    if (error) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-red-500 text-lg">Error loading data: {error}</p>
          </div>
        </div>
      );
    }
    const hasValidData = data && data.metadata && Array.isArray(data.evaluations);
    if (!hasValidData) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-gray-600 text-lg">No data available</p>
          </div>
        </div>
      );
    }
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex flex-wrap items-center gap-3 border-b border-slate-200 pb-4">
            {[
              { id: 'statistics', label: 'Statistics' },
              { id: 'visualizations', label: 'Visualizations' },
              { id: 'welder-performance', label: 'Welder Performance' },
              { id: 'insights', label: 'Insights' },
              { id: 'ml-insights', label: 'AI Insights' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={clsx(
                  'rounded-full px-4 py-1.5 text-sm font-medium transition border border-[#E2E5EA]',
                  activeTab === tab.id
                    ? 'bg-hero-red-10 text-readable-red border-hero-red-20'
                    : 'text-blueprint-blue bg-white border-[#E2E5EA] hover:border-[#C0C5CE] hover:bg-[#FAFBFC]',
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="space-y-6">{renderTabContent()}</div>
        </div>
    );
  };

  return (
    <div className="space-y-5">
      <FilterPanel
        filters={filters}
        selection={selectedFilters}
        onChange={onFilterChange}
        hideProjectFilter={true}
        preserveProjectOnClear={true}
        categoryData={categoryData}
        categoryFields={categoryFields}
        pipeOptions={pipeOptions}
      />

      {contentArea()}
    </div>
  );
};

